import { Container, Flex } from '@lideralia/alife-uikit';
import { Button } from '@lideralia/alife-uikit/dist/atoms/Button';
import IconArrowRight from '@lideralia/alife-uikit/dist/atoms/Icons/IconArrowRight';
import { Loader } from '@lideralia/alife-uikit/dist/atoms/Loader';
import { Text } from '@lideralia/alife-uikit/dist/atoms/Typography';
import Cover from '@lideralia/alife-uikit/dist/atoms/imgs/Cover';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { getActualLanguage } from '../../../../common/functions/lang';
import { initMemory } from '../../actions';
import { MEMORY_STATUS } from '../../config';

export const PresentationScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { idLine, idName } = useParams();
  const lang = getActualLanguage();

  const { memory, status, loadedForLineId } = useSelector((state) => state.biographicalEditor);

  // Redirect if a memory already exists with a meaningful status
  useEffect(() => {
    if (status === 'loading' || status === 'initializing') return;
    if (!memory) return;
    // Guard against stale Redux state from a previously visited line
    if (loadedForLineId !== idLine) return;

    if (memory.status === MEMORY_STATUS.GENERATING) {
      history.replace(`/${lang}/lines/${idLine}/${idName}/biographical-editor/generating`);
    } else if (memory.status === MEMORY_STATUS.COMPLETED) {
      history.replace(`/${lang}/lines/${idLine}/${idName}/biographical-editor/result`);
    } else {
      // draft or ready — go straight to blocks
      history.replace(`/${lang}/lines/${idLine}/${idName}/biographical-editor/blocks`);
    }
  }, [memory, status, loadedForLineId]);

  const handleStart = () => {
    dispatch(initMemory(idLine));
    history.push(`/${lang}/lines/${idLine}/${idName}/biographical-editor/blocks`);
  };

  const handleBack = () => history.goBack();

  // Show loader while memory is being fetched
  if (status === 'loading' || status === 'initializing') {
    return (
      <Container paddingY={3} maxWidth={['400px']}>
        <Flex justifyContent="center" paddingY={5}>
          <Loader />
        </Flex>
      </Container>
    );
  }

  return (
    <>
      {/* Header card */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: '#FCF7EE',
          borderBottom: '1px solid #fac49c',
          boxShadow: '0px 8px 18px 0px rgba(60,54,58,0.1)',
          padding: '18px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div
        style={{
            position: 'absolute',
            width: 86,
            height: 105,
            right: 20,
            bottom: -3
        }}
        >
            <img alt="" style={{ position: 'absolute', display: 'block', width: '100%', height: '100%' }} src="/images/biographical_editor_shape.png" />
        </div>
        <div
        style={{
            position: 'absolute',
            width: 86,
            height: 105,
            right: 20,
            mixBlendMode: 'hard-light',
            bottom: -3
        }}
        >
            <img alt="" style={{ position: 'absolute', display: 'block', width: '100%', height: '100%' }} src="/images/biographical_editor_shape_white.png" />
        </div>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            // use a local image file placed in `public/images` to avoid rate/expiry issues
            backgroundImage: "url('/images/biographical_editor_texture.png')",
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center top',
            backgroundSize: '170px auto'
          }}
        />

        {/* Text */}
        <Text as="h1" sizeText="display24" fontWeight="bold" color="black84" style={{ position: 'relative' }}>
          <FormattedMessage id="biographicalEditor.title" />
        </Text>
        <Text sizeText="display14" color="black60" style={{ position: 'relative', lineHeight: 1.7 }}>
          <FormattedMessage id="biographicalEditor.presentation.header" values={{ br: <br /> }} />
        </Text>
      </div>

      <div style={{ padding: '24px 16px 32px' }}>
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: 18,
            boxShadow: '0px 8px 18px 0px rgba(60,54,58,0.1)',
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            position: 'relative',
          }}
        >
          {/* Illustration */}
          <Cover width={72} height={72} />

          {/* Text */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center', textAlign: 'center', width: '100%' }}>
            <Text sizeText="display16" fontWeight="bold" color="black84">
              <FormattedMessage id="biographicalEditor.biographicalMemory" />
            </Text>
            <Text sizeText="display14" color="black84" style={{ lineHeight: 1.7 }}>
              <FormattedMessage id="biographicalEditor.presentation.cardSubtitle" values={{ br: <br /> }} />
            </Text>
          </div>

          {/* Note */}
          <Text sizeText="display13" color="black60" style={{ textAlign: 'center', lineHeight: '18px' }}>
            <FormattedMessage id="biographicalEditor.presentation.cardNote" values={{ br: <br /> }} />
          </Text>

          {/* CTA */}
          <Button
            id="btn-start-biographical-editor"
            action={handleStart}
            sizeButton="big"
            style={{ width: '100%', borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 , backgroundColor: '#2396FB', color: 'white', padding: '12px'}}
          >
            <FormattedMessage id="biographicalEditor.presentation.cta" />
            <IconArrowRight stroke="white" width={24} height={24} />
          </Button>

          {/* Back */}
          <button
            type="button"
            onClick={handleBack}
            style={{
              width: '100%',
              height: 38,
              backgroundColor: '#E5E5E8',
              borderRadius: 12,
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 700,
              color: '#7F7F8A',
            }}
          >
            <FormattedMessage id="biographicalEditor.presentation.back" />
          </button>

          {/* "Incluido" badge */}
          <div
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              backgroundColor: '#E5E5E8',
              borderRadius: 19,
              padding: '2px 10px',
            }}
          >
            <Text sizeText="display12" fontWeight="bold" color="black60">
              <FormattedMessage id="biographicalEditor.presentation.included" />
            </Text>
          </div>
        </div>
      </div>
    </>
  );
};

export default PresentationScreen;
